import type { CommerceHttpAdapter } from '../../client/CommerceHttpAdapter.js'
import { OfflineError } from '../../client/errors.js'
import type { CacheStrategy } from '../../workers/CacheStrategy.js'
import type { ConnectivityManager } from '../../connectivity/ConnectivityManager.js'
import type { Cart, CartLine, CartSummarySnapshot, CartValidationStatus } from '../../types/index.js'
import { CartSummaryStore } from './CartSummaryStore.js'
import type { WorkerBridge } from '../../workers/WorkerBridge.js'

export class CartModule {
  private readonly summaryStore: CartSummaryStore

  constructor(
    private readonly http: CommerceHttpAdapter,
    private readonly cache: CacheStrategy,
    private readonly connectivity: ConnectivityManager,
    bridge: WorkerBridge
  ) {
    this.summaryStore = new CartSummaryStore(bridge)
  }

  /** Récupère le panier en cours */
  async getCart(): Promise<Cart> {
    const cart = await this.cache.execute(
      'cart:current',
      () => this.http.get<Cart>('/commerce/api/process/cart'),
      'network-first'
    )

    await this.summaryStore.setFromCart(cart)
    return cart
  }

  /** Retourne le dernier résumé panier disponible sans forcer un accès réseau */
  peekSummary(): CartSummarySnapshot | null {
    return this.summaryStore.peek()
  }

  /** Retourne le résumé panier local et lance une revalidation lazy si nécessaire */
  async getSummary(): Promise<CartSummarySnapshot | null> {
    const summary = await this.summaryStore.get()

    if (summary !== null) {
      if (this.summaryStore.isStale(summary) && !this.connectivity.isOffline) {
        void this.refreshSummary()
      }

      return summary
    }

    if (this.connectivity.isOffline) {
      return null
    }

    return this.refreshSummary()
  }

  /** Force le rechargement du panier puis du résumé local */
  async refreshSummary(): Promise<CartSummarySnapshot> {
    const cart = await this.getCart()
    return this.summaryStore.setFromCart(cart)
  }

  /** Supprime le résumé panier local mis en cache côté navigateur */
  async clearSummary(): Promise<void> {
    await this.summaryStore.clear()
  }

  /** Récupère le statut de validation du panier */
  getValidationStatus(): Promise<CartValidationStatus> {
    return this.http.get<CartValidationStatus>('/commerce/api/process/cart/status/check')
  }

  /** Ajoute un article au panier */
  async addItem(reference: string, quantity: number, options?: Record<string, unknown>): Promise<Cart> {
    this.ensureOnline()
    const normalizedReference = reference.trim()
    if (normalizedReference.length === 0) {
      throw new Error('La reference produit est requise pour ajouter un article au panier')
    }

    const normalizedQuantity = Math.max(1, Math.floor(quantity))
    const previousQuantity = await this.getExistingQuantity(normalizedReference, options)
    let cart = await this.http.post<Cart>(this.buildAddItemUrl(normalizedReference, options))

    if (normalizedQuantity > 1 || previousQuantity > 0) {
      const line = this.findLineByReference(cart, normalizedReference, options)
      if (line) {
        cart = await this.http.post<Cart>(`/commerce/api/process/cart/${encodeURIComponent(line.id)}/quantity?qty=${previousQuantity + normalizedQuantity}`)
      }
    }

    await this.cache.execute('cart:current', () => Promise.resolve(cart), 'network-first')
    await this.summaryStore.setFromCart(cart)
    return cart
  }

  /** Met à jour la quantité d'une ligne */
  async updateItem(lineId: string, quantity: number): Promise<Cart> {
    this.ensureOnline()
    const normalizedQuantity = Math.max(0, Math.floor(quantity))
    const cart = await this.http.post<Cart>(`/commerce/api/process/cart/${encodeURIComponent(lineId)}/quantity?qty=${normalizedQuantity}`)
    await this.cache.execute('cart:current', () => Promise.resolve(cart), 'network-first')
    await this.summaryStore.setFromCart(cart)
    return cart
  }

  /** Supprime une ligne du panier */
  async removeItem(lineId: string): Promise<Cart> {
    this.ensureOnline()
    const cart = await this.http.delete<Cart>(`/commerce/api/process/cart/${encodeURIComponent(lineId)}`)
    await this.cache.execute('cart:current', () => Promise.resolve(cart), 'network-first')
    await this.summaryStore.setFromCart(cart)
    return cart
  }

  /** Applique un coupon */
  async applyCoupon(code: string): Promise<Cart> {
    this.ensureOnline()
    const normalizedCode = code.trim()
    if (normalizedCode.length === 0) {
      throw new Error('Le code coupon est requis')
    }

    const cart = await this.http.post<Cart>(`/commerce/api/process/cart/coupons/${encodeURIComponent(normalizedCode)}/add`)
    await this.cache.execute('cart:current', () => Promise.resolve(cart), 'network-first')
    await this.summaryStore.setFromCart(cart)
    return cart
  }

  /** Retire un coupon */
  async removeCoupon(code: string): Promise<Cart> {
    this.ensureOnline()
    const cart = await this.http.delete<Cart>(`/commerce/api/process/cart/coupons/${encodeURIComponent(code)}`)
    await this.cache.execute('cart:current', () => Promise.resolve(cart), 'network-first')
    await this.summaryStore.setFromCart(cart)
    return cart
  }

  private ensureOnline(): void {
    if (this.connectivity.isOffline) {
      throw new OfflineError('Le terminal est hors ligne, les modifications du panier sont indisponibles')
    }
  }

  private async getExistingQuantity(reference: string, options?: Record<string, unknown>): Promise<number> {
    try {
      const cart = await this.getCart()
      const line = this.findLineByReference(cart, reference, options)
      return line?.quantity ?? 0
    } catch {
      return 0
    }
  }

  private findLineByReference(
    cart: Cart | null | undefined,
    reference: string,
    options?: Record<string, unknown>,
  ): CartLine | null {
    const normalizedReference = reference.trim().toUpperCase()
    if (normalizedReference.length === 0) {
      return null
    }

    for (const content of cart?.content ?? []) {
      const isPickupContent = content.contentType === 'StorePickupLines'
      const isPickupRequest = options?.forPickup === true
      if (isPickupContent !== isPickupRequest) {
        continue
      }

      for (const line of content.lines ?? []) {
        if ((line.reference ?? '').trim().toUpperCase() === normalizedReference) {
          return line
        }
      }
    }

    return null
  }

  private buildAddItemUrl(reference: string, options?: Record<string, unknown>): string {
    const query = new URLSearchParams()

    for (const [key, value] of Object.entries(options ?? {})) {
      if (value === undefined || value === null) {
        continue
      }

      if (Array.isArray(value)) {
        for (const entry of value) {
          if (entry !== undefined && entry !== null) {
            query.append(key, String(entry))
          }
        }

        continue
      }

      query.append(key, String(value))
    }

    const queryString = query.toString()
    return `/commerce/api/process/cart/add/${encodeURIComponent(reference)}${queryString ? `?${queryString}` : ''}`
  }
}
